/**
 * Serviço para verificar descontos de clientes
 * Endpoint: POST /nivel-desconto/check-discount
 */

import { api, APIError, type ApiOverrideOptions } from "../../../../lib/api";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import { handleToolsApiError } from "../../../../lib/toolsErrorHandler";
import { apiWithRetry } from "../../../../lib/apiRetry";
import { QuotationOperationContextStore } from "./quotationOperationContext";

export interface CheckDiscountRequest {
	cli_id: number;
	pro_id: number;
	one1_outro_id?: number | null;
}

export interface CheckDiscountResponse {
	has_discount: boolean;
	discount_id?: number;
	discount_value?: number | null;
	product_base_price: number;
	final_price: number;
	product_info: {
		pro_id: number;
		pro_nome: string;
		pro_categoria_item: number;
	};
	subcategory_id?: number | null;
	color_id?: number | null;
	message?: string;
}

export interface CheckDiscountError {
	error: string;
	message: string;
	categoria_id?: number;
	produto_info?: {
		pro_id: number;
		pro_nome: string;
		pro_categoria_item: number;
	};
}

/**
 * Verifica se um cliente tem desconto para um produto
 */
/**
 * Verifica se um cliente tem desconto para um produto
 *
 * ✅ PADRONIZADO: Usa api() helper que já trata refresh de accessToken automaticamente
 * Esta rota não é /tools/*, então não precisa de SSO token
 */
export async function checkDiscount(
	request: CheckDiscountRequest,
): Promise<CheckDiscountResponse> {
	const endpoint = "/nivel-desconto/check-discount";

	sendToTerminal(
		"🔍 [QUOTATION] Verificando desconto do cliente",
		{
			cli_id: request.cli_id,
			pro_id: request.pro_id,
			one1_outro_id: request.one1_outro_id,
		},
		LogCategory.BUSINESS,
	);

	try {
		const operationOverride = QuotationOperationContextStore.getOverride();
		const apiOverride: ApiOverrideOptions = operationOverride
			? {
					accessTokenOverride: operationOverride.accessToken,
					tenantIdOverride: operationOverride.tenantId,
				}
			: {};

		// ✅ MIGRADO: Usar apiWithRetry para retry automático em 502/504
		const data: CheckDiscountResponse = await apiWithRetry(
			endpoint,
			{
				method: "POST",
				body: JSON.stringify({
					cli_id: request.cli_id,
					pro_id: request.pro_id,
					one1_outro_id: request.one1_outro_id ?? undefined,
				}),
			},
			undefined,
			apiOverride,
		);

		sendToTerminal(
			"✅ [QUOTATION] Desconto verificado",
			{
				has_discount: data.has_discount,
				final_price: data.final_price,
				product_base_price: data.product_base_price,
				discount_value: data.discount_value,
			},
			LogCategory.BUSINESS,
		);

		return data;
	} catch (error) {
		// ✅ MIGRADO: Usar handleToolsApiError para melhor tratamento
		if (error instanceof APIError) {
			// Criar novo APIError com metadata enriquecido (evitar mutação)
			const enhancedError = new APIError(
				error.status,
				error.message,
				error.kind,
				{ ...error.data, url: endpoint },
			);

			// Converter para AppError usando handler centralizado
			const appError = handleToolsApiError(enhancedError, "discount");

			sendToTerminal(
				"❌ [QUOTATION] Falha ao verificar desconto",
				{
					error: appError.message,
					errorCode: appError.code,
					status: error.status,
				},
				LogCategory.ERROR,
			);
		} else {
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			sendToTerminal(
				"❌ [QUOTATION] Falha ao verificar desconto",
				{
					error: errorMessage,
				},
				LogCategory.ERROR,
			);
		}
		throw error;
	}
}
